import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { FileText, Plus, Search, Filter } from 'lucide-react';

const STATUS_STYLES = {
  draft: 'bg-slate-800 text-slate-400',
  sent: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  partially_signed: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  completed: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  expired: 'bg-red-500/15 text-red-400 border border-red-500/30',
  voided: 'bg-slate-700 text-slate-500',
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    api.get(`/dashboard/documents?${params}&limit=50`)
      .then(r => setDocs(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status]);

  const filtered = search
    ? docs.filter(d =>
        d.title?.toLowerCase().includes(search.toLowerCase()) ||
        d.client_name?.toLowerCase().includes(search.toLowerCase())
      )
    : docs;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Documents</h1>
          <p className="text-slate-500 text-sm mt-0.5">{docs.length} total</p>
        </div>
        <button
          onClick={() => navigate('/documents/new')}
          className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus size={15} /> Send Document
        </button>
      </div>

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="bg-slate-900 border border-slate-700 rounded-xl pl-8 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>
        {['','sent','partially_signed','completed','expired','voided'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
              status === s ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            {s ? s.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <FileText size={32} className="mx-auto mb-3 opacity-30" />
            <p>No documents found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                {['Document','Client','Company','Status','Signers','Last Activity'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filtered.map(doc => (
                <tr
                  key={doc.id}
                  onClick={() => navigate(`/documents/${doc.id}`)}
                  className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-white text-sm truncate max-w-[200px]">{doc.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{doc.sender_name}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300">{doc.client_name || '—'}</td>
                  <td className="px-6 py-4 text-sm text-slate-400">{doc.sending_company}</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLES[doc.status]}`}>
                      {doc.status?.replace(/_/g,' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-400">
                    {doc.signed_count}/{doc.total_signers}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {doc.last_activity ? formatDistanceToNow(new Date(doc.last_activity), { addSuffix: true }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
